/**
 * SAML 2.0 identity-provider helpers.
 *
 * Scope: Prism's realm in Keycloak acting as a SAML **Service Provider**, brokering
 * authentication out to a customer's external SAML **Identity Provider** (Microsoft
 * Entra ID, Okta, Google Workspace, ADFS). The customer's IdP asserts, Keycloak
 * consumes, and Prism links the resulting login to an existing Prism account by
 * email. Everything here is pure: no React, no network, no DOM beyond
 * `window.crypto.subtle`.
 *
 * NOT to be confused with `src/utils/mapperFields.js`, which covers Custom SAML
 * Applications -- the opposite direction, where Keycloak is the **IdP** and a
 * customer application is the SP, and where the "mappers" are SAML *protocol*
 * mappers on a Keycloak client. The two features share the word "SAML" and
 * nothing else: different Keycloak APIs, different Prism pages, no shared state.
 * Do not merge them.
 */

/**
 * Hard cap on a metadata document accepted from the file picker or the XML
 * textarea. Mirrors utils.MaxSAMLMetadataSize on the backend (512 KiB), which is
 * also what `http.MaxBytesReader` enforces on the import endpoint. Checking it
 * client-side turns a 400 into an instant inline message.
 */
export const MAX_METADATA_BYTES = 512 * 1024;

/**
 * Entra ID's email claim. Sourced from `user.mail`, which is EMPTY for
 * mailbox-less users and most B2B guests -- which is precisely why it is the
 * mapper attribute (email is how Prism matches an existing account) and NOT the
 * principal (a null principal is a hard 400 at login).
 */
export const ENTRA_EMAIL_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress';

/**
 * Entra ID's UPN claim. Always present for every user in the tenant, so this is
 * the principal attribute under the Entra rule (principalType = ATTRIBUTE).
 * Despite the ".../claims/name" path this is the userPrincipalName, not a
 * display name.
 */
export const ENTRA_UPN_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';

/*
 * The four NameID formats Entra ID will actually honour.
 *
 * CRITICAL: these span TWO SAML versions and the version is part of the URN.
 *   - persistent   -> SAML **2.0**
 *   - emailAddress -> SAML **1.1**
 *   - unspecified  -> SAML **1.1**
 *   - transient    -> SAML **2.0**
 *
 * A hand-written `urn:oasis:names:tc:SAML:2.0:nameid-format:emailAddress` looks
 * entirely reasonable and fails EVERY Entra login (AADSTS75016, invalid
 * NameIDPolicy). Always reference these constants; never retype a URN inline.
 */
export const NAMEID_FORMAT_PERSISTENT = 'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent';
export const NAMEID_FORMAT_EMAIL_ADDRESS = 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress';
export const NAMEID_FORMAT_UNSPECIFIED = 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified';
export const NAMEID_FORMAT_TRANSIENT = 'urn:oasis:names:tc:SAML:2.0:nameid-format:transient';

/** Options for the NameID Policy Format <Select> in the advanced accordion. */
export const SAML_NAMEID_FORMATS = [
  {
    value: NAMEID_FORMAT_PERSISTENT,
    label: 'Persistent (SAML 2.0)',
    helperText: "Entra's default when it publishes no NameIDFormat. The NameID is an opaque per-application GUID, so the principal must come from an attribute.",
  },
  {
    value: NAMEID_FORMAT_EMAIL_ADDRESS,
    label: 'Email address (SAML 1.1)',
    helperText: 'The NameID carries the email address itself. Note the SAML 1.1 URN -- the 2.0 spelling of this format does not exist.',
  },
  {
    value: NAMEID_FORMAT_UNSPECIFIED,
    label: 'Unspecified (SAML 1.1)',
    helperText: 'Leaves the format to the identity provider. Also a SAML 1.1 URN.',
  },
  {
    value: NAMEID_FORMAT_TRANSIENT,
    label: 'Transient (SAML 2.0)',
    helperText: 'A new NameID on every login. Cannot be used with Principal Type = SUBJECT -- Keycloak rejects that pair with a 500.',
  },
];

/** Principal types Keycloak accepts. ATTRIBUTE_NAME is NOT one of them: Keycloak
 *  swallows the parse failure and silently falls back to SUBJECT. */
const PRINCIPAL_TYPE_SUBJECT = 'SUBJECT';
const PRINCIPAL_TYPE_ATTRIBUTE = 'ATTRIBUTE';
const PRINCIPAL_TYPE_FRIENDLY_ATTRIBUTE = 'FRIENDLY_ATTRIBUTE';

/** True when the principal is read from an assertion attribute rather than the
 *  <Subject> NameID, i.e. when principalAttribute is required. */
const isAttributePrincipal = (principalType) =>
  principalType === PRINCIPAL_TYPE_ATTRIBUTE || principalType === PRINCIPAL_TYPE_FRIENDLY_ATTRIBUTE;

/**
 * Initial form values for a new SAML provider. Mirrors the create-side defaults
 * in the backend's buildSAMLConfigMap so that what the admin sees before saving
 * is what actually gets written.
 *
 * The manual-entry default pair is SUBJECT + the SAML 1.1 emailAddress URN: the
 * one combination where the NameID itself is the email, so nothing else has to
 * be configured for account matching to work. An Entra import overwrites both.
 */
export const SAML_DEFAULTS = {
  displayName: '',

  // Import-only scratch fields. Never part of the API payload.
  metadataUrl: '',
  metadataXml: '',

  // Endpoints and trust
  entityId: '', // blank = let the backend compute {publicOrigin}/realms/{realm}
  idpEntityId: '',
  singleSignOnServiceUrl: '',
  signingCertificate: '',
  metadataDescriptorUrl: '',

  // Identity mapping
  nameIDPolicyFormat: NAMEID_FORMAT_EMAIL_ADDRESS,
  principalType: PRINCIPAL_TYPE_SUBJECT,
  principalAttribute: '',
  emailAttributeName: ENTRA_EMAIL_CLAIM,

  // Protocol options
  signatureAlgorithm: 'RSA_SHA256', // the only algorithm Entra verifies
  validateSignature: true,
  wantAssertionsSigned: true, // matches Entra's default
  wantAuthnRequestsSigned: false, // Entra publishes no WantAuthnRequestsSigned
  forceAuthn: false,
  postBindingResponse: true,
  postBindingAuthnRequest: true,
  allowedClockSkew: 30, // seconds; Entra adds zero buffer to NotBefore

  enabled: true,
};

/**
 * Cheap client-side pre-check before spending a round trip on an import.
 *
 * Deliberately a substring test, not a parse: the authoritative check is the
 * backend's xml.Decoder walk (DOCTYPE/ENTITY rejection, aggregate rejection,
 * token and depth caps). This only catches the obvious mistakes -- pasting SP
 * metadata, pasting a login page, pasting an empty file -- so the admin gets an
 * answer immediately instead of a 400.
 *
 * Namespace-prefix agnostic: Entra emits bare <EntityDescriptor>, others emit
 * <md:EntityDescriptor>.
 *
 * @param {string} xml
 * @returns {boolean}
 */
export const looksLikeSamlMetadata = (xml) => {
  if (typeof xml !== 'string' || !xml.trim()) return false;

  const hasEntityDescriptor = /<(?:[A-Za-z0-9._-]+:)?EntityDescriptor[\s/>]/.test(xml);
  const hasIdpDescriptor = /<(?:[A-Za-z0-9._-]+:)?IDPSSODescriptor[\s/>]/.test(xml);

  return hasEntityDescriptor && hasIdpDescriptor;
};

/**
 * Reduce a signing certificate to the bare base64 Keycloak requires.
 *
 * PEM armor is fatal rather than tolerated: Keycloak's
 * XMLSignatureUtil.getX509CertificateFromKeyInfoString prepends its own
 * "-----BEGIN CERTIFICATE-----", so a stored full PEM produces nested headers, a
 * ProcessingException, and a 500 at the ACS endpoint. Line breaks (LF or CRLF,
 * depending on where the admin copied from) are stripped for the same reason.
 *
 * Certificates are comma-joined for rotation, so each element is normalized
 * independently and the commas are preserved. Base64 has no comma, so splitting
 * on it is unambiguous.
 *
 * The backend runs the same normalization plus x509.ParseCertificate and is the
 * authority; this exists so the dialog can compute thumbprints from whatever the
 * admin pasted.
 *
 * @param {string} raw
 * @returns {string} bare base64, comma-joined, no whitespace
 */
export const normalizeCertificate = (raw) => {
  if (typeof raw !== 'string' || !raw.trim()) return '';

  return raw
    .split(',')
    .map((element) =>
      element
        // Strip any PEM armor line: BEGIN/END CERTIFICATE, BEGIN/END TRUSTED CERTIFICATE, ...
        .replace(/-{3,}\s*(?:BEGIN|END)[^-]*-{3,}/gi, '')
        // Strip ALL whitespace, including the CRLFs a Windows copy/paste brings along
        .replace(/\s+/g, '')
    )
    .filter((element) => element !== '')
    .join(',');
};

/*
 * Fields the backend's import can populate, in dialog order, with the labels the
 * "what got filled" summary uses.
 *
 * displayName is deliberately absent: it is the admin's choice, drives alias
 * generation, and the metadata document has no say in it. An import must never
 * overwrite what the admin already typed.
 */
const IMPORTABLE_FIELDS = [
  { key: 'idpEntityId', label: 'IdP Entity ID' },
  { key: 'singleSignOnServiceUrl', label: 'Sign-on URL' },
  { key: 'signingCertificate', label: 'Signing certificate' },
  { key: 'metadataDescriptorUrl', label: 'Metadata URL' },
  { key: 'entityId', label: 'SP Entity ID' },
  { key: 'nameIDPolicyFormat', label: 'NameID policy format' },
  { key: 'principalType', label: 'Principal type' },
  { key: 'principalAttribute', label: 'Principal attribute' },
  { key: 'emailAttributeName', label: 'Email claim attribute' },
  { key: 'signatureAlgorithm', label: 'Signature algorithm' },
  { key: 'validateSignature', label: 'Validate signature' },
  { key: 'wantAssertionsSigned', label: 'Want assertions signed' },
  { key: 'wantAuthnRequestsSigned', label: 'Want AuthnRequests signed' },
  { key: 'forceAuthn', label: 'Force re-authentication' },
  { key: 'postBindingResponse', label: 'POST binding for responses' },
  { key: 'postBindingAuthnRequest', label: 'POST binding for AuthnRequests' },
  { key: 'allowedClockSkew', label: 'Allowed clock skew' },
];

/**
 * Merge an import result into the dialog's form state.
 *
 * The import endpoint returns `request`, an ALREADY-TYPED SAMLIdPRequest -- the
 * Keycloak-config-key to DTO-field translation happens once, on the backend, in
 * a tested function. So there is nothing to parse here: this is a shallow merge
 * plus the human-readable summary the dialog shows afterwards.
 *
 * Absent, null and empty-string values are skipped so a sparse import (Okta
 * publishes no principal hints, for instance) cannot blank out fields the admin
 * already filled in.
 *
 * @param {object} importedRequest - `response.request` from POST .../import-metadata
 * @param {object} current - the dialog's current formData
 * @returns {{ next: object, filled: string[], warnings: string[] }}
 */
export const mapImportedRequestToFormData = (importedRequest, current = {}) => {
  const imported = importedRequest || {};
  const base = current || {};
  const next = { ...base };
  const filled = [];
  const warnings = [];

  IMPORTABLE_FIELDS.forEach(({ key, label }) => {
    const value = imported[key];
    if (value === undefined || value === null) return;
    if (typeof value === 'string' && value.trim() === '') return;

    next[key] = typeof value === 'string' ? value.trim() : value;
    filled.push(label);
  });

  if (!next.signingCertificate) {
    warnings.push(
      'No signing certificate was found in the metadata. Paste one in before saving, or every login will fail signature validation.'
    );
  }

  if (!next.singleSignOnServiceUrl) {
    warnings.push(
      'No sign-on URL was found in the metadata. This document may describe a Service Provider rather than an Identity Provider.'
    );
  }

  // Only meaningful while editing an existing provider, where base.principalType
  // is the value users have already federated against.
  if (base.principalType && imported.principalType && base.principalType !== imported.principalType) {
    warnings.push(
      `Principal Type changes from ${base.principalType} to ${imported.principalType}. Changing this on a provider people already sign in with orphans every existing federated link.`
    );
  }

  return { next, filled, warnings };
};

/** Coerce a form value to a boolean, tolerating the "true"/"false" strings a
 *  round trip through Keycloak's string-valued config map produces. */
const toBool = (value, fallback) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
};

const trimmed = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

/** allowedClockSkew is a number on the wire (*int in the DTO), never a string.
 *  A blank field means "use the default", not zero. */
const toClockSkew = (raw) => {
  const asText = trimmed(raw);
  if (asText === '') return SAML_DEFAULTS.allowedClockSkew;

  const parsed = Number(asText);
  return Number.isFinite(parsed) ? parsed : SAML_DEFAULTS.allowedClockSkew;
};

/**
 * Build the request body for POST/PUT .../identity-providers/saml.
 *
 * Whitelist-built, one key at a time. That is what makes the three never-write
 * keys structurally impossible to emit rather than merely unlikely:
 *   - `singleLogoutServiceUrl` -- single logout is not wired in this release, and
 *     a half-configured SLO endpoint produces logout failures that present as
 *     login failures.
 *   - `hideOnLoginPage` -- the legacy config key OVERRIDES the top-level
 *     hideOnLogin, and both login themes branch on `social.providers` being
 *     non-empty, so setting it yields no SSO button AND a password form on an
 *     IdP-only realm.
 *   - `useMetadataDescriptorUrl` -- turns every login into an outbound fetch to a
 *     customer-controlled URL, bypasses the pinned signingCertificate entirely,
 *     and makes a cold cache a login outage.
 *
 * @param {object} formData
 * @param {{ editing?: boolean }} options
 * @returns {object} the API payload
 */
export const buildSamlPayload = (formData = {}, { editing = false } = {}) => {
  const data = formData || {};

  const principalType = trimmed(data.principalType) || SAML_DEFAULTS.principalType;
  const emailAttributeName = trimmed(data.emailAttributeName);

  // When the principal comes from an attribute, principalAttribute is REQUIRED --
  // Keycloak resolves a null principal into a hard 400 at login. If the admin
  // left it blank, the email claim they already supplied is the only sensible
  // stand-in. Never do this for SUBJECT: there the principal is the NameID and a
  // principalAttribute would be ignored at best, misleading at worst.
  let principalAttribute = trimmed(data.principalAttribute);
  if (!principalAttribute && emailAttributeName && isAttributePrincipal(principalType)) {
    principalAttribute = emailAttributeName;
  }

  const payload = {
    displayName: trimmed(data.displayName),

    idpEntityId: trimmed(data.idpEntityId),
    singleSignOnServiceUrl: trimmed(data.singleSignOnServiceUrl),

    nameIDPolicyFormat: trimmed(data.nameIDPolicyFormat) || SAML_DEFAULTS.nameIDPolicyFormat,
    principalType,
    principalAttribute,
    emailAttributeName,

    signatureAlgorithm: trimmed(data.signatureAlgorithm) || SAML_DEFAULTS.signatureAlgorithm,
    validateSignature: toBool(data.validateSignature, SAML_DEFAULTS.validateSignature),
    wantAssertionsSigned: toBool(data.wantAssertionsSigned, SAML_DEFAULTS.wantAssertionsSigned),
    wantAuthnRequestsSigned: toBool(data.wantAuthnRequestsSigned, SAML_DEFAULTS.wantAuthnRequestsSigned),
    forceAuthn: toBool(data.forceAuthn, SAML_DEFAULTS.forceAuthn),
    postBindingResponse: toBool(data.postBindingResponse, SAML_DEFAULTS.postBindingResponse),
    postBindingAuthnRequest: toBool(data.postBindingAuthnRequest, SAML_DEFAULTS.postBindingAuthnRequest),
    allowedClockSkew: toClockSkew(data.allowedClockSkew),
  };

  // Half of the cert-wipe defence (the backend's read-modify-write merge is the
  // other half). Keycloak's PUT is a full replace and its removeEmptyString()
  // DELETES keys whose value is "", so a certificate that goes missing on an edit
  // is gone while validateSignature stays true and every login fails.
  //
  // The edit dialog prefills signingCertificate UNMASKED -- certificates are not
  // secrets and Keycloak does not mask them on GET, unlike OIDC client secrets --
  // so the value is always present and is always sent back. The omit-when-blank
  // branch below is therefore unreachable from the edit form; it exists so that a
  // literal "" can never reach the wire, because an empty string and an absent
  // key mean the same thing to the backend (unchanged) and only one of them is
  // honest about it.
  const signingCertificate = trimmed(data.signingCertificate);
  if (signingCertificate) {
    payload.signingCertificate = signingCertificate;
  }

  // Optional: blank means "let the backend compute {publicOrigin}/realms/{realm}",
  // which is what we want. Sending "" would not.
  const entityId = trimmed(data.entityId);
  if (entityId) {
    payload.entityId = entityId;
  }

  // Recorded on an import-from-URL. Informational only -- it enables the
  // "Re-import signing certificate" affordance and is never paired with
  // useMetadataDescriptorUrl.
  const metadataDescriptorUrl = trimmed(data.metadataDescriptorUrl);
  if (metadataDescriptorUrl) {
    payload.metadataDescriptorUrl = metadataDescriptorUrl;
  }

  // On update Keycloak's PUT is a full replace, so `enabled` has to be stated
  // explicitly or the backend has to fall back to the stored value. State it. On
  // create the backend's default (true) is correct and there is nothing to lose.
  if (editing) {
    payload.enabled = toBool(data.enabled, true);
  }

  return payload;
};

/*
 * Strings api.js manufactures when it has no server message to relay
 * (src/services/api.js:320, :343, :352). They tell the admin nothing about their
 * metadata document, so they get replaced. Matched by PREFIX because two of the
 * three carry a trailing sentence or the underlying fetch error.
 *
 * Everything else -- crucially the backend's own import diagnostics ("document
 * contains no IDPSSODescriptor", "EntitiesDescriptor aggregates are not
 * supported", the SSRF rejections) and the read-only-mode refusal -- passes
 * through verbatim. Those messages are the entire value of the import endpoint.
 */
const GENERIC_API_FAILURE_PREFIXES = [
  'Operation failed',
  'An unexpected error occurred',
  'API request failed',
];

/**
 * Turn an import rejection into something an admin can act on.
 *
 * @param {Error|string} err
 * @param {'url'|'xml'} mode - which half of the import dialog was in use
 * @returns {string}
 */
export const describeImportFailure = (err, mode) => {
  const raw = typeof err === 'string' ? err : (err && err.message) || '';
  const message = raw.trim();

  const source = mode === 'xml' ? 'the metadata document' : 'the metadata URL';

  if (!message) {
    return `Prism could not import ${source}. Please try again.`;
  }

  if (GENERIC_API_FAILURE_PREFIXES.some((prefix) => message.startsWith(prefix))) {
    return `Prism could not reach the server to import ${source}. Check your connection and try again.`;
  }

  return message;
};

const HEX = '0123456789ABCDEF';

/** Base64 -> Uint8Array, using the platform decoder. Returns null on anything
 *  that is not decodable base64 rather than throwing. */
const decodeBase64 = (value) => {
  // `globalThis` is ES2020 and eslint-config-react-app declares only the es6
  // environment, so referencing it fails the production build with no-undef.
  // The bare `atob` identifier is a declared browser/worker global and covers
  // exactly the same fallback case.
  const decoder =
    typeof window !== 'undefined' && window.atob
      ? window.atob
      : typeof atob !== 'undefined'
        ? atob
        : null;
  if (!decoder) return null;

  try {
    const binary = decoder(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch (e) {
    return null;
  }
};

const toHex = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    out += HEX[bytes[i] >> 4] + HEX[bytes[i] & 0x0f];
  }
  return out;
};

/**
 * SHA-1 thumbprints for a signing certificate value, ONE PER comma-separated
 * certificate -- a real metadata import legitimately carries two during a
 * rotation window, and showing a single merged value would be a lie.
 *
 * SHA-1 is not a security decision here: it is the digest Entra ID, Okta and the
 * Windows certificate dialog all display as "Thumbprint", so it is the only value
 * an admin can actually compare against their IdP. Output is uppercase hex with
 * no separators, matching Entra's rendering.
 *
 * Returns [] rather than throwing when SubtleCrypto is unavailable. That happens
 * on any non-secure context (plain http, e.g. a local dev host that is not
 * localhost), where the thumbprint chips are simply not rendered -- a missing
 * convenience must never break the dialog.
 *
 * @param {string} base64OrCommaJoined - bare base64 or full PEM, single or comma-joined
 * @returns {Promise<string[]>}
 */
export const certificateFingerprints = async (base64OrCommaJoined) => {
  const subtle = typeof window !== 'undefined' ? window.crypto?.subtle : undefined;
  if (!subtle) return [];

  const normalized = normalizeCertificate(base64OrCommaJoined);
  if (!normalized) return [];

  const fingerprints = [];

  for (const element of normalized.split(',')) {
    const bytes = decodeBase64(element);
    // Skip anything that is not decodable base64. The backend rejects it with a
    // real error on save; here it just means one fewer chip.
    if (!bytes || bytes.length === 0) continue;

    try {
      // eslint-disable-next-line no-await-in-loop
      const digest = await subtle.digest('SHA-1', bytes);
      fingerprints.push(toHex(digest));
    } catch (e) {
      // Same reasoning: a digest failure costs a chip, never the dialog.
    }
  }

  return fingerprints;
};
